import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Shield, Eye, Check, PenLine, TrendingUp, TrendingDown, Bitcoin, Compass, Sun, Moon, Volume2, VolumeX, Lock, Unlink } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { AnimatedLogo } from '../components/shared/AnimatedLogo';
import { FREE_ITEM_CAP, FREE_ITEM_CAP_WORD, LAUNCH_PRICING_PREVIEW, PAYMENTS_ENABLED, PRICING, REFUND_DAYS, STORAGE_KEYS, formatPrice } from '../config/constants';
import { getLaunchPricingAvailable } from '../services/stripeService';

// ─── Scroll Animation Helpers ───────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0 },
};

const slideInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const ScrollReveal: React.FC<{
  children: React.ReactNode;
  variants?: Record<string, Record<string, number>>;
  className?: string;
  delay?: number;
}> = ({ children, variants = fadeUp, className, delay = 0 }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={variants}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerReveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ─── Design Tokens ───────────────────────────────────────────────
interface Tokens {
  dark: boolean;
  surface: string;
  surfaceAlt: string;
  text1: string;      // primary text
  text2: string;      // secondary text
  text3: string;      // muted text
  card: string;       // card surface
  glass: string;      // glass panel
  divider: string;    // section divider border
  rowBg: string;      // table/list row background
  labelColor: string; // small label text
  valueColor: string; // value/heading text
}

const buildTokens = (darkMode: boolean): Tokens => ({
  dark: darkMode,
  surface: darkMode ? 'bg-slate-950' : 'bg-[#f8f9ff]',
  surfaceAlt: darkMode ? 'bg-slate-900' : 'bg-[#eff4ff]',
  text1: darkMode ? 'text-slate-50' : 'text-slate-900',
  text2: darkMode ? 'text-slate-400' : 'text-slate-600',
  text3: darkMode ? 'text-slate-500' : 'text-slate-500',
  card: darkMode
    ? 'bg-slate-800/50 border border-slate-700/30'
    : 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100',
  glass: darkMode
    ? 'bg-slate-800/70 backdrop-blur-3xl border border-slate-700/50'
    : 'bg-white/70 backdrop-blur-3xl border border-white/40',
  divider: darkMode ? 'border-slate-800' : 'border-slate-200',
  rowBg: darkMode ? 'bg-slate-700/30' : 'bg-slate-50',
  labelColor: darkMode ? 'text-slate-400' : 'text-slate-500',
  valueColor: darkMode ? 'text-slate-50' : 'text-slate-900',
});

// ─── Shared CSS ──────────────────────────────────────────────────
const SECTION_PX = 'px-8 md:px-8';
const SECTION_PY = 'py-14 md:py-20';
const SECTION_BASE = `${SECTION_PY} ${SECTION_PX}`;
const HEADING = 'text-3xl md:text-4xl font-extrabold tracking-tight mb-4';
const GRADIENT = 'bg-gradient-to-br from-[#6b38d4] to-[#8455ef]';

const ctaButtonClass = `${GRADIENT} text-white shadow-xl shadow-violet-500/20 hover:scale-105`;

// The Pro CTA follows PAYMENTS_ENABLED. The unavailable state keeps the
// full-strength gradient and white label so the text stays readable (dimming
// it failed contrast); only the shadow and hover lift drop away.
const proCtaButtonClass = PAYMENTS_ENABLED
  ? `${GRADIENT} text-white shadow-xl shadow-violet-500/20 hover:scale-105`
  : `${GRADIENT} text-white cursor-not-allowed`;

// ─── Landing Page ────────────────────────────────────────────────
export type MarketingPage = 'home' | 'features' | 'pricing' | 'login';

// Public URLs served by the landing page. scripts/prerender.mjs renders each
// one to its own HTML file, and vercel.json rewrites to those files.
export const MARKETING_PATHS: Record<string, MarketingPage> = {
  '/': 'home',
  '/features': 'features',
  '/pricing': 'pricing',
  '/login': 'login',
};

interface LandingPageProps {
  onSignIn: () => void;
  page?: MarketingPage;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onSignIn, page = 'home' }) => {
  const { darkMode, toggleDarkMode } = useTheme();
  const t = buildTokens(darkMode);
  const [activeSection, setActiveSection] = useState<MarketingPage>(page);
  const navLinkClass = (target: MarketingPage) =>
    `hidden sm:inline-block px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
      activeSection === target ? 'text-violet-500' : `${t.text2} hover:text-violet-500`
    }`;

  // /features and /pricing stay separate URLs for search engines, but render
  // the full landing page and open scrolled to their section.
  useEffect(() => {
    if (page === 'features' || page === 'pricing') {
      document.getElementById(page)?.scrollIntoView();
    }
  }, [page]);

  useEffect(() => {
    if (page === 'login') return;
    const onPopState = () => {
      const target = MARKETING_PATHS[window.location.pathname] ?? 'home';
      setActiveSection(target);
      if (target === 'features' || target === 'pricing') {
        document.getElementById(target)?.scrollIntoView();
      } else {
        window.scrollTo({ top: 0 });
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [page]);

  const goToSection = (target: 'home' | 'features' | 'pricing') => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (page === 'login') return;
    e.preventDefault();
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';
    if (target === 'home') {
      window.scrollTo({ top: 0, behavior });
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior });
    }
    setActiveSection(target);
    const path = target === 'home' ? '/' : `/${target}`;
    if (window.location.pathname !== path) window.history.pushState(null, '', path);
  };

  // Choosing Pro here survives the Google redirect: once signed in (and past
  // the terms screen) the app sends the user straight to Stripe checkout.
  // Which price they saw is kept too, so checkout never charges a different
  // amount without telling them first.
  const handleProSignIn = (sawLaunchPrice: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEYS.UPGRADE_INTENT, sawLaunchPrice ? 'launch' : 'regular');
    } catch {
      /* storage unavailable: they still get signed in, just without the prompt */
    }
    onSignIn();
  };

  return (
    <div className={`min-h-screen ${t.surface} antialiased`}>
      <div className={`sticky top-0 z-40`}>
      {/* Navigation */}
      <header className={`${t.dark ? 'bg-slate-950/80 border-b border-slate-800' : 'bg-white/80'} backdrop-blur-xl shadow-[0_32px_64px_rgba(107,56,212,0.06)]`}>
        <nav className={`flex justify-between items-center ${SECTION_PX} py-4 max-w-7xl mx-auto`}>
          <a href="/" onClick={goToSection('home')} aria-label="TallyUp Finance home" className="flex items-center gap-2 md:gap-3">
            <AnimatedLogo className="h-9 w-9 md:h-10 md:w-10" />
            <div>
              <div className={`text-xl md:text-2xl font-black leading-tight tracking-tight ${t.text1}`}>
                TallyUp
              </div>
              <p className="text-[10px] md:text-xs font-semibold tracking-[0.2em] text-violet-500 pl-0.5">
                Finance
              </p>
            </div>
          </a>
          <div className="flex items-center gap-2 md:gap-3">
            <a href="/features" onClick={goToSection('features')} className={navLinkClass('features')}>Features</a>
            <a href="/pricing" onClick={goToSection('pricing')} className={navLinkClass('pricing')}>Pricing</a>
            <button
              type="button"
              onClick={toggleDarkMode}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={darkMode ? 'Light mode' : 'Dark mode'}
              className={`p-2.5 rounded-xl transition-colors ${
                t.dark
                  ? 'text-slate-300 hover:text-white hover:bg-slate-800'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
              }`}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={onSignIn}
              className={`${GRADIENT} text-white px-5 md:px-6 py-2 md:py-2.5 rounded-xl font-bold text-sm transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-violet-500/20`}
            >
              Sign In
            </button>
          </div>
        </nav>
      </header>
      </div>

      <main className="overflow-hidden">
        {page === 'login' && <SignInPanel t={t} onSignIn={onSignIn} />}

        {page !== 'login' && (
        <section className={`${SECTION_PX} py-10 md:py-24 max-w-7xl mx-auto`}>
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <motion.div
              className="space-y-8 px-4 md:px-0"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.h1 variants={fadeUp} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className={`text-5xl md:text-7xl font-extrabold leading-[1.08] tracking-tight ${t.text1}`}>
                Your money.<br/>
                Your data.<br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6b38d4] to-[#8455ef]">Your control.</span>
              </motion.h1>
              <motion.p variants={fadeUp} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className={`text-lg md:text-xl md:max-w-md leading-relaxed ${t.text2}`}>
                Track every dollar intentionally.<br className="md:hidden" /> From investments and crypto to retirement projections, see your full financial picture with privacy-first<br className="md:hidden" /> tools built for serious investors.
              </motion.p>
              <motion.div variants={fadeUp} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="flex items-center gap-4">
                <button
                  onClick={onSignIn}
                  className={`px-7 md:px-8 py-3.5 md:py-4 rounded-xl font-bold text-base md:text-lg transition-all flex items-center gap-2 ${ctaButtonClass}`}
                >
                  Get Started Free
                  <ArrowRight size={20} />
                </button>
              </motion.div>
              <motion.p variants={fadeUp} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className={`flex items-center gap-2 text-sm font-medium ${t.text2}`}>
                <Lock size={16} className="text-violet-500 flex-shrink-0" />
                No bank logins. No account linking.
              </motion.p>
            </motion.div>

            <motion.div
              className="relative group"
              initial={{ opacity: 0, x: 80, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
            >
              <div className={`${t.glass} rounded-2xl p-8 md:p-10 shadow-2xl relative z-10`}>
                <DashboardPreview t={t} />
              </div>
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-violet-500/10 rounded-full blur-3xl -z-10" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-slate-500/10 rounded-full blur-3xl -z-10" />
            </motion.div>
          </div>
        </section>
        )}

        {page !== 'login' && (<>
        {/* Promo video */}
        <section id="features" className={`${SECTION_BASE} ${t.surfaceAlt} scroll-mt-20`}>
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <SectionHeader t={t} title="See TallyUp in Action" subtitle="A quick tour of your full financial picture, all in one view." />
            </ScrollReveal>
            <ScrollReveal>
              <div className={`${t.glass} rounded-2xl p-2 md:p-3 shadow-2xl`}>
                <PromoVideo />
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Core Features */}
        <section className={`${SECTION_BASE}`}>
          <div className="max-w-7xl mx-auto">
            <ScrollReveal>
              <SectionHeader t={t} title="Intentional by Design" subtitle="We've stripped away the noise to focus on what matters: your long-term wealth building journey." />
            </ScrollReveal>
            <StaggerReveal className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              <motion.div variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <FeatureCard t={t} icon={<Shield size={24} />} title="Privacy First"
                  description="Your financial data lives in your own Google Sheet, in your own Drive. TallyUp reads and writes it. TallyUp never stores it." />
              </motion.div>
              <motion.div variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <FeatureCard t={t} icon={<PenLine size={24} />} title="Intentional Tracking"
                  description="Manually log your finances in minutes. Research suggests this builds lasting awareness and better money habits." />
              </motion.div>
              <motion.div variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <FeatureCard t={t} icon={<Compass size={24} />} title="Retirement Projection"
                  description="See how your real portfolio grows over time with per-asset growth rates, scenario modeling, and withdrawal planning." />
              </motion.div>
              <motion.div variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
                <FeatureCard t={t} icon={<Eye size={24} />} title="Full Transparency"
                  description="Open your Google Sheet anytime. No black box, you own the data, you see everything." />
              </motion.div>
            </StaggerReveal>
          </div>
        </section>

        <ManualVsAutoSection t={t} />
        <HowItWorksSection t={t} />
        <ScienceSection t={t} />
        <SneakPeekSection t={t} />
        </>)}

        {page !== 'login' && <FounderSection t={t} />}
        {page !== 'login' && (
          <PricingSection t={t} onSignIn={onSignIn} onProSignIn={handleProSignIn} />
        )}
        {page !== 'login' && <CTASection t={t} onSignIn={onSignIn} />}
      </main>

      {/* Footer */}
      <footer className={`py-10 md:py-12 ${SECTION_PX} mt-12 md:mt-16 border-t ${t.divider} ${t.dark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 max-w-7xl mx-auto text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <div className={`text-lg font-bold whitespace-nowrap ${t.text1}`}>TallyUp Finance</div>
            <span className={`hidden md:inline ${t.text3}`}>|</span>
            <nav className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
              <a href="/features" onClick={goToSection('features')} className={`text-sm whitespace-nowrap ${t.text3} hover:text-violet-500 transition-colors`}>Features</a>
              <span className={t.text3}>|</span>
              <a href="/pricing" onClick={goToSection('pricing')} className={`text-sm whitespace-nowrap ${t.text3} hover:text-violet-500 transition-colors`}>Pricing</a>
              <span className={t.text3}>|</span>
              <a href="/privacy" className={`text-sm whitespace-nowrap ${t.text3} hover:text-violet-500 transition-colors`}>Privacy Policy</a>
              <span className={t.text3}>|</span>
              <a href="/terms" className={`text-sm whitespace-nowrap ${t.text3} hover:text-violet-500 transition-colors`}>Terms of Service</a>
            </nav>
          </div>
          <p className={`text-sm ${t.text3}`}>
            &copy; 2026 TallyUp Finance.
            <span className="block sm:inline"> Your money. Your data. Your control.</span>
          </p>
        </div>
      </footer>

    </div>
  );
};

// ─── Shared Sub-Components ───────────────────────────────────────

const PROMO_UNMUTED_VOLUME = 0.4;

// One is picked at random on each page load. No poster: the video plays as
// it scrolls into view, so the frame behind it is its own opening colour.
// The promo fades in from white over its first quarter second, so it starts
// (and loops back to) just past that fade.
const PROMO_VIDEOS = [
  { src: '/tallyup-promo.mp4', background: '#01061E', start: 0.27 },
  { src: '/tallyup-commercial-v5.mp4', background: 'linear-gradient(135deg, #EEF3FC, #E4DFF8 55%, #E1E6F5)', start: 0 },
];

const PromoVideo: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);
  const [promo] = useState(() => PROMO_VIDEOS[Math.floor(Math.random() * PROMO_VIDEOS.length)]);
  const [reducedMotion] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  );
  // Chrome lays a grey panel over a video with controls until it has a frame,
  // hiding the background colour, so the controls appear once it is playing.
  // Without autoplay (reduced motion) they are the only way to start it.
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reducedMotion) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.5 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const toggleSound = () => {
    const video = videoRef.current;
    if (!video) return;
    const nextMuted = !video.muted;
    if (!nextMuted) {
      video.volume = PROMO_UNMUTED_VOLUME;
      video.play().catch(() => {});
    }
    video.muted = nextMuted;
  };

  return (
    <div className="relative">
      <video
        ref={videoRef}
        className="w-full aspect-video rounded-xl"
        style={{ background: promo.background }}
        src={promo.start ? `${promo.src}#t=${promo.start}` : promo.src}
        muted
        loop={!promo.start}
        onEnded={e => {
          e.currentTarget.currentTime = promo.start;
          e.currentTarget.play().catch(() => {});
        }}
        controls={started || reducedMotion}
        onPlaying={() => setStarted(true)}
        playsInline
        preload="none"
        onVolumeChange={e => setMuted(e.currentTarget.muted)}
      />
      <button
        type="button"
        onClick={toggleSound}
        aria-label={muted ? 'Unmute video' : 'Mute video'}
        className="absolute top-3 right-3 md:top-4 md:right-4 flex items-center gap-2 px-3 py-2 rounded-full bg-black/60 hover:bg-black/75 text-white text-sm font-semibold backdrop-blur-sm transition-colors"
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        {muted && <span>Tap for sound</span>}
      </button>
    </div>
  );
};

const SectionHeader: React.FC<{ t: Tokens; title: string; subtitle?: string; as?: 'h1' | 'h2' }> = ({ t, title, subtitle, as: Heading = 'h2' }) => (
  <div className="text-center mb-8 md:mb-12">
    <Heading className={`${HEADING} ${t.text1}`}>{title}</Heading>
    {subtitle && <p className={`${t.text2} max-w-2xl mx-auto text-base md:text-lg`}>{subtitle}</p>}
  </div>
);

const SignInPanel: React.FC<{ t: Tokens; onSignIn: () => void }> = ({ t, onSignIn }) => (
  <section className={`${SECTION_BASE} min-h-[60vh] flex items-center`}>
    <div className={`w-full max-w-md mx-auto rounded-2xl p-8 md:p-10 text-center ${t.card}`}>
      <div className="flex justify-center mb-6">
        <AnimatedLogo className="h-14 w-14" />
      </div>
      <h1 className={`text-3xl font-extrabold tracking-tight mb-3 ${t.text1}`}>Sign in to TallyUp</h1>
      <p className={`mb-8 leading-relaxed ${t.text2}`}>
        Sign in with your Google account. Your financial data stays in a Google Sheet in your own Drive.
      </p>
      <button
        type="button"
        onClick={onSignIn}
        className={`w-full px-6 py-3.5 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2 ${ctaButtonClass}`}
      >
        Continue with Google
        <ArrowRight size={20} />
      </button>
      <p className={`mt-6 text-sm ${t.text3}`}>
        New to TallyUp? Signing in creates your free account.
      </p>
      <p className={`mt-4 text-sm ${t.text3}`}>
        <a href="/privacy" className="hover:text-violet-500 transition-colors">Privacy Policy</a>
        {' · '}
        <a href="/terms" className="hover:text-violet-500 transition-colors">Terms of Service</a>
      </p>
    </div>
  </section>
);

const FeatureCard: React.FC<{ t: Tokens; icon: React.ReactNode; title: string; description: string }> = ({ t, icon, title, description }) => (
  <div className={`p-7 md:p-8 rounded-2xl ${t.card} hover:shadow-xl transition-shadow group h-full`}>
    <div className={`w-14 h-14 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-500 mb-6 group-hover:${GRADIENT} group-hover:text-white transition-colors`}>
      {icon}
    </div>
    <h3 className={`text-xl font-bold mb-3 ${t.text1}`}>{title}</h3>
    <p className={`text-sm leading-relaxed ${t.text2}`}>{description}</p>
  </div>
);

// ─── Dashboard Preview ───────────────────────────────────────────

const DashboardPreview: React.FC<{ t: Tokens }> = ({ t }) => {
  const subtleBg = t.dark ? 'bg-slate-700/40' : 'bg-[#eff4ff]';

  return (
    <div className="space-y-6">
      <div>
        <p className={`text-xs font-semibold uppercase tracking-widest mb-1 ${t.labelColor}`}>Total Net Worth</p>
        <div className="flex items-center gap-3">
          <h2 className={`text-4xl md:text-5xl font-black tracking-tighter ${t.valueColor}`}>$254,100</h2>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500 text-xs font-bold">
            <TrendingUp size={12} />+12.4%
          </span>
        </div>
      </div>

      <div>
        <div className={`flex justify-between text-sm font-bold mb-2 ${t.valueColor}`}>
          <span>Emergency Fund</span>
          <span>85%</span>
        </div>
        <div className={`h-3 w-full ${subtleBg} rounded-full overflow-hidden`}>
          <div className={`h-full ${GRADIENT.replace('bg-gradient-to-br', 'bg-gradient-to-r')} w-[85%] rounded-full`} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Liquid Cash', value: '$12,400' },
          { label: 'Investments', value: '$85,300' },
          { label: '401k', value: '$62,400' },
          { label: 'Home Equity', value: '$94,000' },
        ].map(card => (
          <div key={card.label} className={`${subtleBg} p-4 rounded-xl`}>
            <p className={`text-xs font-bold mb-1 ${t.labelColor}`}>{card.label}</p>
            <p className={`text-lg font-bold ${t.valueColor}`}>{card.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Sneak Peek Panels ───────────────────────────────────────────

const SNEAK_PEEK_TABS = ['Investments', 'Crypto', 'Retirement', 'Credit Cards', 'Charts'] as const;

const InvestmentsPanel: React.FC<{ t: Tokens }> = ({ t }) => {
  const stocks = [
    { symbol: 'AAPL', name: 'Apple Inc.', shares: 25, price: 273.96, change: 1.8 },
    { symbol: 'MSFT', name: 'Microsoft', shares: 15, price: 384.47, change: 0.6 },
    { symbol: 'TSLA', name: 'Tesla Inc.', shares: 10, price: 399.83, change: -2.1 },
    { symbol: 'VOO', name: 'Vanguard S&P', shares: 30, price: 632.31, change: 0.7 },
  ];
  const headerColor = t.dark ? 'text-slate-500' : 'text-slate-400';
  return (
    <div className="space-y-2">
      <div className={`grid grid-cols-4 gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wide ${headerColor}`}>
        <span>Symbol</span><span className="text-right">Shares</span><span className="text-right">Price</span><span className="text-right">Change</span>
      </div>
      {stocks.map(s => (
        <div key={s.symbol} className={`grid grid-cols-4 gap-2 items-center px-3 py-2.5 rounded-xl ${t.rowBg}`}>
          <div className="min-w-0 overflow-hidden whitespace-nowrap">
            <span className={`text-sm font-bold ${t.valueColor}`}>{s.symbol}</span>
            <span className={`text-xs ml-2 hidden sm:inline ${headerColor}`}>{s.name}</span>
          </div>
          <span className={`text-sm text-right ${t.dark ? 'text-slate-300' : 'text-slate-700'}`}>{s.shares}</span>
          <span className={`text-sm font-medium text-right ${t.dark ? 'text-slate-200' : 'text-slate-800'}`}>${s.price.toLocaleString()}</span>
          <div className="flex items-center justify-end gap-1">
            {s.change >= 0 ? <TrendingUp size={13} className="text-emerald-500" /> : <TrendingDown size={13} className="text-red-500" />}
            <span className={`text-xs font-bold ${s.change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{s.change >= 0 ? '+' : ''}{s.change}%</span>
          </div>
        </div>
      ))}
      <div className={`text-right pt-2 pr-1 text-xs font-medium ${t.text3}`}>
        Total Value: <span className="font-bold text-violet-500">${stocks.reduce((sum, s) => sum + s.shares * s.price, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
    </div>
  );
};

const CryptoPanel: React.FC<{ t: Tokens }> = ({ t }) => {
  const coins = [
    { symbol: 'BTC', name: 'Bitcoin', amount: 0.45, value: 43650, change: 4.2 },
    { symbol: 'ETH', name: 'Ethereum', amount: 3.2, value: 11200, change: -1.8 },
    { symbol: 'SOL', name: 'Solana', amount: 50, value: 7500, change: 8.6 },
  ];
  return (
    <div className="space-y-3">
      {coins.map(c => (
        <div key={c.symbol} className={`flex items-center justify-between p-3 rounded-xl ${t.rowBg}`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
              c.symbol === 'BTC' ? 'bg-orange-500/15 text-orange-400' :
              c.symbol === 'ETH' ? 'bg-blue-500/15 text-blue-400' :
              'bg-violet-500/15 text-violet-400'
            }`}>
              {c.symbol === 'BTC' ? <Bitcoin size={18} /> : c.symbol === 'ETH' ? 'ETH' : 'SOL'}
            </div>
            <div>
              <div className={`text-sm font-bold ${t.valueColor}`}>{c.name}</div>
              <div className={`text-xs ${t.text3}`}>{c.amount} {c.symbol}</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-sm font-bold ${t.dark ? 'text-slate-200' : 'text-slate-800'}`}>${c.value.toLocaleString()}</div>
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
              c.change >= 0 ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'
            }`}>{c.change >= 0 ? '+' : ''}{c.change}%</span>
          </div>
        </div>
      ))}
      <div className={`text-right pt-1 pr-1 text-xs font-medium ${t.text3}`}>
        Portfolio: <span className="font-bold text-violet-500">${coins.reduce((sum, c) => sum + c.value, 0).toLocaleString()}</span>
      </div>
    </div>
  );
};

const CreditCardsPanel: React.FC<{ t: Tokens }> = ({ t }) => {
  const cards = [
    { name: 'Chase Sapphire', balance: 1240, limit: 10000 },
    { name: 'Amex Gold', balance: 3800, limit: 5000 },
    { name: 'Citi Double Cash', balance: 420, limit: 8000 },
  ];
  const trackBg = t.dark ? 'bg-slate-600' : 'bg-slate-200';
  const getBarColor = (pct: number) => pct < 30 ? 'bg-emerald-500' : pct < 70 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="space-y-3">
      {cards.map(c => {
        const pct = Math.round((c.balance / c.limit) * 100);
        return (
          <div key={c.name} className={`p-3 rounded-xl ${t.rowBg}`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`text-sm font-bold ${t.valueColor}`}>{c.name}</span>
              <span className={`text-xs font-medium ${t.labelColor}`}>
                ${c.balance.toLocaleString()} / ${c.limit.toLocaleString()}
              </span>
            </div>
            <div className={`h-2.5 rounded-full ${trackBg} overflow-hidden`}>
              <div className={`h-full rounded-full ${getBarColor(pct)}`} style={{ width: `${pct}%` }} />
            </div>
            <div className={`text-right text-[10px] mt-1 font-medium ${
              pct < 30 ? 'text-emerald-500' : pct < 70 ? 'text-amber-500' : 'text-red-500'
            }`}>{pct}% utilization</div>
          </div>
        );
      })}
    </div>
  );
};

const RetirementPanel: React.FC<{ t: Tokens }> = ({ t }) => {
  const years = [2026, 2030, 2035, 2040, 2045, 2050];
  const values = [250, 380, 620, 950, 1400, 2100];
  const maxV = 2200;
  const chartW = 500; const chartH = 160;
  const padL = 40; const padR = 10; const padT = 14; const padB = 24;
  const plotW = chartW - padL - padR; const plotH = chartH - padT - padB;
  const points = values.map((v, i) => {
    const x = padL + (i / (values.length - 1)) * plotW;
    const y = padT + plotH - (v / maxV) * plotH;
    return `${x},${y}`;
  });
  const statBg = (color: string) => t.dark ? `bg-${color}-500/10` : `bg-${color}-50`;
  const statLabel = (color: string) => t.dark ? `text-${color}-400/70` : `text-${color}-500`;
  const statValue = (color: string) => t.dark ? `text-${color}-300` : `text-${color}-700`;
  const axisColor = t.dark ? 'fill-slate-500' : 'fill-slate-400';
  const gridStroke = t.dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  return (
    <div>
      <div className={`text-xs font-semibold mb-3 ${t.labelColor}`}>Retirement Projection</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { color: 'violet', label: 'Nest Egg', value: '$2.1M' },
          { color: 'emerald', label: 'Runway', value: 'Indefinite' },
          { color: 'amber', label: 'Annual Income', value: '$84K' },
        ].map(s => (
          <div key={s.label} className={`rounded-lg p-2.5 ${statBg(s.color)}`}>
            <div className={`text-[9px] uppercase tracking-wider ${statLabel(s.color)}`}>{s.label}</div>
            <div className={`text-sm font-bold ${statValue(s.color)}`}>{s.value}</div>
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full">
        <defs>
          <linearGradient id="retireFill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="retireLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#14b8a6" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        {[0, 500, 1000, 1500, 2000].map(v => {
          const y = padT + plotH - (v / maxV) * plotH;
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke={gridStroke} />
              <text x={padL - 4} y={y + 3} textAnchor="end" className={`text-[9px] ${axisColor}`}>
                ${v > 0 ? `${(v / 1000).toFixed(1)}M` : '0'}
              </text>
            </g>
          );
        })}
        {years.map((yr, i) => {
          const x = padL + (i / (years.length - 1)) * plotW;
          return <text key={yr} x={x} y={chartH - 4} textAnchor="middle" className={`text-[8px] ${axisColor}`}>{yr}</text>;
        })}
        <polygon points={`${padL},${padT + plotH} ${points.join(' ')} ${padL + plotW},${padT + plotH}`} fill="url(#retireFill)" />
        <polyline points={points.join(' ')} fill="none" stroke="url(#retireLine)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {(() => {
          const retireX = padL + (4 / (years.length - 1)) * plotW;
          const retireY = padT + plotH - (values[4] / maxV) * plotH;
          const markerColor = t.dark ? '#fbbf24' : '#d97706';
          const markerStroke = t.dark ? '#1e293b' : '#ffffff';
          return (
            <>
              <line x1={retireX} y1={padT} x2={retireX} y2={padT + plotH} stroke={t.dark ? 'rgba(251,191,36,0.4)' : 'rgba(217,119,6,0.3)'} strokeWidth="1" strokeDasharray="3 3" />
              <circle cx={retireX} cy={retireY} r="3" fill={markerColor} stroke={markerStroke} strokeWidth="1.5" />
              <text x={retireX} y={padT - 3} textAnchor="middle" fill={markerColor} fontSize="8" fontWeight="600">Retire 2045</text>
            </>
          );
        })()}
      </svg>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {['Stocks', 'Crypto', '401(k)', 'Cash'].map(label => (
          <span key={label} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-500/15 text-violet-500 border border-violet-500/20">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const ChartsPanel: React.FC<{ t: Tokens }> = ({ t }) => {
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
  const values = [195, 198, 205, 210, 208, 215, 222, 230, 228, 238, 245, 250];
  const maxV = 260; const minV = 190;
  const chartW = 500; const chartH = 180;
  const padL = 40; const padR = 10; const padT = 10; const padB = 24;
  const plotW = chartW - padL - padR; const plotH = chartH - padT - padB;
  const points = values.map((v, i) => {
    const x = padL + (i / (values.length - 1)) * plotW;
    const y = padT + plotH - ((v - minV) / (maxV - minV)) * plotH;
    return `${x},${y}`;
  });
  const fillPoints = `${padL},${padT + plotH} ${points.join(' ')} ${padL + plotW},${padT + plotH}`;
  const axisColor = t.dark ? 'fill-slate-500' : 'fill-slate-400';
  const gridStroke = t.dark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';

  return (
    <div>
      <div className={`text-xs font-semibold mb-2 ${t.labelColor}`}>Net Worth, Last 12 Months (in thousands)</div>
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full">
        {[190, 210, 230, 250].map(v => {
          const y = padT + plotH - ((v - minV) / (maxV - minV)) * plotH;
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={padL + plotW} y2={y} stroke={gridStroke} />
              <text x={padL - 4} y={y + 3} textAnchor="end" className={`text-[9px] ${axisColor}`}>${v}k</text>
            </g>
          );
        })}
        {months.map((m, i) => {
          const x = padL + (i / (months.length - 1)) * plotW;
          return <text key={m} x={x} y={chartH - 4} textAnchor="middle" className={`text-[8px] ${axisColor}`}>{m}</text>;
        })}
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={fillPoints} fill="url(#chartFill)" />
        <polyline points={points.join(' ')} fill="none" stroke="#a855f7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {(() => {
          const last = points[points.length - 1].split(',');
          return <circle cx={last[0]} cy={last[1]} r="3" fill="#a855f7" />;
        })()}
      </svg>
    </div>
  );
};

// ─── Section Components ──────────────────────────────────────────

const SneakPeekSection: React.FC<{ t: Tokens }> = ({ t }) => {
  const [activeTab, setActiveTab] = useState(0);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const panels = [InvestmentsPanel, CryptoPanel, RetirementPanel, CreditCardsPanel, ChartsPanel];
  const pillBg = t.dark ? 'bg-slate-800' : 'bg-slate-100';

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.offsetWidth - 2);
  };

  React.useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scrollToTab = (index: number) => {
    setActiveTab(index);
    const container = scrollRef.current;
    if (!container) return;
    const buttons = container.querySelectorAll('button');
    const btn = buttons[index];
    if (btn) {
      const scrollLeft = btn.offsetLeft - container.offsetWidth / 2 + btn.offsetWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
    setTimeout(checkScroll, 300);
  };

  const scrollBy = (dir: number) => {
    const next = activeTab + dir;
    if (next >= 0 && next < SNEAK_PEEK_TABS.length) scrollToTab(next);
  };

  return (
    <section className={`${SECTION_BASE} ${t.surfaceAlt}`}>
      <div className="max-w-7xl mx-auto">
      <ScrollReveal>
        <SectionHeader t={t} title="See What's Inside" subtitle="Your dashboard tracks every corner of your finances. Here's a peek." />
      </ScrollReveal>
      <ScrollReveal delay={0.15}>
      <div className="flex justify-center mb-8">
        <div className={`relative ${pillBg} p-1.5 rounded-full flex items-center max-w-full`}>
          {canScrollLeft && (
            <button
              onClick={() => scrollBy(-1)}
              className={`flex-shrink-0 p-2 rounded-full transition-all z-10 border shadow-sm opacity-60 hover:opacity-100 ${
                t.dark ? 'text-slate-300 border-slate-600 bg-slate-700/50 hover:bg-slate-600/70' : 'text-slate-500 border-slate-300/60 bg-white/50 hover:bg-white/80'
              }`}
              aria-label="Previous tab"
            >
              <ArrowRight size={14} className="rotate-180" />
            </button>
          )}
          <div
            ref={scrollRef}
            className="flex overflow-x-auto"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onScroll={checkScroll}
          >
            {SNEAK_PEEK_TABS.map((tab, i) => (
              <button
                key={tab}
                onClick={() => scrollToTab(i)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all whitespace-nowrap flex-shrink-0 ${
                  activeTab === i
                    ? 'bg-white shadow-sm text-violet-600'
                    : t.dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          {canScrollRight && (
            <button
              onClick={() => scrollBy(1)}
              className={`flex-shrink-0 p-2 rounded-full transition-all z-10 border shadow-sm opacity-60 hover:opacity-100 ${
                t.dark ? 'text-slate-300 border-slate-600 bg-slate-700/50 hover:bg-slate-600/70' : 'text-slate-500 border-slate-300/60 bg-white/50 hover:bg-white/80'
              }`}
              aria-label="Next tab"
            >
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
      <div className={`max-w-2xl mx-auto rounded-2xl ${t.card} p-6 md:p-8`}>
        <div className="grid">
          {panels.map((Panel, i) => (
            <div key={i} className={`col-start-1 row-start-1 ${i === activeTab ? '' : 'invisible'}`}>
              <Panel t={t} />
            </div>
          ))}
        </div>
      </div>
      </ScrollReveal>
      </div>
    </section>
  );
};

const ManualVsAutoSection: React.FC<{ t: Tokens }> = ({ t }) => (
  <section className={`${SECTION_BASE}`}>
    <ScrollReveal>
      <div className="max-w-4xl mx-auto text-center mb-10 md:mb-14">
        <h2 className={`${HEADING} ${t.text1}`}>
          You control your data. We handle the prices.
        </h2>
        <p className={`text-base md:text-lg ${t.text2}`}>
          You manually enter your accounts and holdings for full control. TallyUp automatically updates stock, ETF, and crypto prices so your dashboard stays current.
        </p>
      </div>
    </ScrollReveal>
    <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
      {[
        { title: 'You Enter', icon: '\u{1F512}', items: ['Account names & balances', 'Holdings & quantities', 'Your financial picture'], variant: slideInLeft },
        { title: 'We Update', icon: '\u26A1', items: ['Stock & ETF prices', 'Crypto prices', 'Market data, automatically'], variant: slideInRight },
      ].map((col, idx) => (
        <ScrollReveal key={col.title} variants={col.variant} delay={idx * 0.15}>
          <div className={`rounded-2xl ${t.card} overflow-hidden`}>
            <div className={`px-6 py-4 ${t.dark ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
              <h3 className={`text-lg font-bold ${t.text1}`}>
                <span className="mr-2">{col.icon}</span>{col.title}
              </h3>
            </div>
            <ul>
              {col.items.map((item, i) => (
                <li key={item} className={`px-6 py-3 text-sm md:text-base ${t.text2} ${
                  i < col.items.length - 1 ? `border-b ${t.dark ? 'border-slate-700/50' : 'border-slate-100'}` : ''
                }`}>{item}</li>
              ))}
            </ul>
          </div>
        </ScrollReveal>
      ))}
    </div>
    <ScrollReveal delay={0.2}>
      <div className={`max-w-4xl mx-auto mt-6 md:mt-8 rounded-2xl ${t.card} p-6 md:p-8 flex flex-col md:flex-row gap-5 md:gap-6`}>
        <div className="w-12 h-12 rounded-xl bg-violet-500/10 text-violet-500 flex items-center justify-center flex-shrink-0">
          <Unlink size={24} />
        </div>
        <div>
          <h3 className={`text-lg md:text-xl font-bold mb-2 ${t.text1}`}>No bank or QuickBooks linking, by design</h3>
          <p className={`text-sm md:text-base leading-relaxed ${t.text2}`}>
            TallyUp doesn&apos;t connect to your bank, credit cards, brokerage, or QuickBooks, and it doesn&apos;t use
            bank-linking services like Plaid or other account aggregators. Linking an account means handing a third
            party your login, or a standing key that lets it read every transaction. TallyUp never asks for either.
            It&apos;s just your Google account and your own spreadsheet, so your financial data doesn&apos;t pass
            through anyone else. That isn&apos;t a missing feature or a paid add-on. It&apos;s the point.
          </p>
        </div>
      </div>
    </ScrollReveal>
  </section>
);

const HowItWorksSection: React.FC<{ t: Tokens }> = ({ t }) => (
  <section className={`${SECTION_BASE} ${t.surfaceAlt}`}>
    <ScrollReveal>
      <h2 className={`${HEADING} text-center mb-16 md:mb-20 max-w-7xl mx-auto ${t.text1}`}>
        How It Works
      </h2>
    </ScrollReveal>
    <StaggerReveal className="flex flex-col md:flex-row justify-between gap-10 md:gap-12 max-w-7xl mx-auto">
      {[
        { num: '1', title: 'Sign In', desc: 'Connect with your Google account' },
        { num: '2', title: 'Auto Setup', desc: 'We create a template spreadsheet in your Drive' },
        { num: '3', title: 'Track Intentionally', desc: 'Spend 2 minutes daily logging your accounts' },
        { num: '4', title: 'Watch Growth', desc: 'Beautiful dashboard shows your progress' },
      ].map((step, i) => (
        <motion.div key={step.num} variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="flex-1 text-center">
          <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center text-xl md:text-2xl font-black mx-auto mb-6 md:mb-8 ${
            i === 0
              ? `${GRADIENT} text-white shadow-lg shadow-violet-500/30`
              : 'bg-violet-500/10 text-violet-500'
          }`}>{step.num}</div>
          <h4 className={`text-lg md:text-xl font-bold mb-2 ${t.text1}`}>{step.title}</h4>
          <p className={`text-sm ${t.text2}`}>{step.desc}</p>
        </motion.div>
      ))}
    </StaggerReveal>
  </section>
);

const ScienceSection: React.FC<{ t: Tokens }> = ({ t }) => (
  <section className={`${SECTION_BASE}`}>
    <div className="max-w-6xl mx-auto">
      <ScrollReveal>
        <SectionHeader t={t} title="The Science of Manual Tracking" subtitle="Writing down your finances isn't busywork. Research suggests it can help build awareness and better habits." />
      </ScrollReveal>
      <StaggerReveal className="grid md:grid-cols-2 gap-6 md:gap-8">
        {[
          { stat: '33%', title: 'More Likely to Achieve Goals', desc: 'People who write down their financial goals are 33% more likely to achieve them.', source: 'Dr. Gail Matthews, Dominican University' },
          { stat: '20%', title: 'Better Budget Adherence', desc: 'Households that track their spending are 20% more likely to stick to a budget.', source: 'National Endowment for Financial Education, 2019' },
          { stat: '43%', title: 'Consistent Savers', desc: '43% of people who track finances report building savings consistently.', source: 'YouGov Survey, 2017' },
          { stat: '2x', title: 'Better Emergency Preparedness', desc: 'Households with a written financial plan are twice as likely to weather unexpected expenses.', source: 'Federal Reserve Report, 2021' },
        ].map(item => (
          <motion.div key={item.title} variants={fadeUp} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className={`p-6 rounded-2xl ${t.dark ? 'bg-white/5 border border-white/10' : 'bg-violet-50/80 border border-violet-100'}`}>
            <div className={`text-4xl md:text-5xl font-bold mb-3 ${t.dark ? 'text-violet-400' : 'text-violet-600'}`}>
              {item.stat}
            </div>
            <p className={`text-base md:text-lg font-semibold mb-2 ${t.text1}`}>
              {item.title}
            </p>
            <p className={`text-sm md:text-base ${t.text2}`}>
              {item.desc}
            </p>
            <p className={`text-xs mt-2 ${t.text3}`}>
              {item.source}
            </p>
          </motion.div>
        ))}
      </StaggerReveal>
      <ScrollReveal delay={0.3}>
        <p className={`text-xs text-center mt-8 ${t.text3}`}>
          These statistics reflect published research on financial tracking habits. Individual results vary. This is informational content, not a guarantee of financial outcomes.
        </p>
      </ScrollReveal>
    </div>
  </section>
);

const FounderSection: React.FC<{ t: Tokens }> = ({ t }) => (
  <section className={`${SECTION_BASE}`}>
    <ScrollReveal>
      <div className={`max-w-4xl mx-auto rounded-3xl backdrop-blur-xl ${t.card} p-8 md:p-12`}>
        <div className="max-w-3xl mx-auto text-left">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 md:mb-8 ${t.text1}`}>
            Why I Built This
          </h2>
          <blockquote className={`text-lg md:text-xl leading-relaxed italic ${t.text2}`}>
            "I built TallyUp Finance because I was tired of giving my bank credentials to apps I didn't trust. I wanted a simple way to track my money intentionally, without algorithms watching, without selling my data, and without losing control. Your spreadsheet is yours. TallyUp never sees it. Your money. Your data. Your control."
          </blockquote>
          <div className="mt-6 md:mt-8 text-right">
            <p className={`text-base md:text-lg font-semibold ${t.text1}`}>
              — Jesse Moore, Founder
            </p>
          </div>
        </div>
      </div>
    </ScrollReveal>
  </section>
);

const FREE_FEATURES = [
  'Beautiful dashboard',
  `${FREE_ITEM_CAP} accounts, investments, 401k, crypto & private equity`,
  'Unlimited credit cards, assets, liabilities & HSA',
  'Retirement projection (5-year horizon) & all four calculators',
  'Budget, goals, net worth trend & asset allocation',
  '12 months of net worth history',
  'Your data in your own Google Sheet',
];

const PRO_FEATURES = [
  'Everything in Free, plus:',
  'Unlimited accounts and holdings',
  'Unlimited net worth history',
  'Full charts and analytics',
  'PDF reports',
  'Custom market snapshot tickers',
  'Priority support',
];

const PricingCardShell: React.FC<{
  t: Tokens;
  featured?: boolean;
  badge?: string;
  children: React.ReactNode;
}> = ({ t, featured, badge, children }) => (
  <motion.div
    variants={fadeUp}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`${t.card} p-8 rounded-2xl border-2 flex flex-col relative ${
      featured ? 'border-violet-500' : 'border-transparent'
    }`}
  >
    {badge && (
      <div className={`absolute -top-3 left-8 ${GRADIENT} text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest`}>
        {badge}
      </div>
    )}
    {children}
  </motion.div>
);

const PricingFeatures: React.FC<{ t: Tokens; items: string[] }> = ({ t, items }) => (
  <ul className="space-y-3 mb-8">
    {items.map((item, i) => (
      <li key={item} className={`flex items-start gap-3 text-sm ${i === 0 && item.endsWith(':') ? `font-semibold ${t.text1}` : t.text2}`}>
        <Check size={18} className="text-violet-500 flex-shrink-0 mt-0.5" />{item}
      </li>
    ))}
  </ul>
);

/**
 * Both cards share this skeleton so their price rows and feature lists sit on
 * the same baselines. Every block is a fixed or minimum height — anything that
 * sizes to its content pushes the card below it out of alignment.
 */
const PRICE_ROW = 'h-[3.5rem] flex items-end';
const CAPTION_BLOCK = 'min-h-[5rem] pt-2.5';

const PricingSection: React.FC<{ t: Tokens; onSignIn: () => void; onProSignIn: (sawLaunchPrice: boolean) => void }> = ({ t, onSignIn, onProSignIn }) => {
  const [launchAvailable, setLaunchAvailable] = useState(LAUNCH_PRICING_PREVIEW);

  // Never render the early adopter price unless Stripe can still honor it.
  // The price on this page has to be the price on the checkout screen.
  useEffect(() => {
    if (!PAYMENTS_ENABLED || LAUNCH_PRICING_PREVIEW) return;
    let cancelled = false;
    getLaunchPricingAvailable().then((available) => {
      if (!cancelled) setLaunchAvailable(available);
    });
    return () => { cancelled = true; };
  }, []);

  const proPrice = launchAvailable ? PRICING.launch : PRICING.pro;
  const proCta = PAYMENTS_ENABLED ? 'Get Pro' : 'Pro coming soon';
  const secondaryButtonClass = 'border-2 border-violet-500 text-violet-500 hover:bg-violet-500/5';

  // Early adopter framing only: no seat count, no deadline, nothing that
  // implies a cap. The regular price stays visible (struck through) so it
  // remains the anchor once the coupon is gone.
  const priceCaption = launchAvailable
    ? `Early adopter pricing. Regularly ${formatPrice(PRICING.pro)}.`
    : 'One-time payment.';

  return (
  <section id="pricing" className={`${SECTION_BASE} ${t.surfaceAlt} scroll-mt-20`}>
    <div className="max-w-4xl mx-auto">
      <ScrollReveal>
        <SectionHeader t={t} as="h2" title="Simple, Transparent Pricing" subtitle={`${FREE_ITEM_CAP_WORD} of everything free. Unlimited on Pro. Pay once, no subscription.`} />
      </ScrollReveal>

      <StaggerReveal className="grid md:grid-cols-2 gap-6 items-stretch">
        {/* Free */}
        <PricingCardShell t={t}>
          <h3 className={`text-2xl font-bold ${t.text1}`}>Free</h3>
          <p className={`text-sm mt-1 mb-5 ${t.text2}`}>{`Everything, capped at ${FREE_ITEM_CAP_WORD.toLowerCase()}.`}</p>

          <div className={`${PRICE_ROW} ${t.text1}`}>
            <span className="text-5xl font-black tracking-tight tabular-nums">{formatPrice(0)}</span>
          </div>

          <div className={CAPTION_BLOCK}>
            <p className={`text-sm ${t.text2}`}>Free forever.</p>
            <p className={`text-xs mt-1 leading-relaxed ${t.text3}`}>
              No card required. Upgrade whenever you outgrow the caps.
            </p>
          </div>

          <PricingFeatures t={t} items={FREE_FEATURES} />
          <button
            onClick={onSignIn}
            className={`w-full mt-auto py-4 rounded-xl font-bold transition-colors ${secondaryButtonClass}`}
          >
            Start Free
          </button>
        </PricingCardShell>

        {/* Pro */}
        <PricingCardShell t={t} featured badge={launchAvailable ? 'Early Adopter' : 'Most Popular'}>
          <h3 className={`text-2xl font-bold ${t.text1}`}>Pro</h3>
          <p className={`text-sm mt-1 mb-5 ${t.text2}`}>No caps on anything.</p>

          <div className={`${PRICE_ROW} ${t.text1}`}>
            {launchAvailable && (
              <span className={`text-2xl font-bold line-through pb-2 mr-2.5 tabular-nums ${t.text3}`}>
                {formatPrice(PRICING.pro)}
              </span>
            )}
            <motion.span
              key={`pro-${launchAvailable}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl font-black tracking-tight tabular-nums"
            >
              {formatPrice(proPrice)}
            </motion.span>
            <span className={`text-base font-normal pb-1.5 ml-1 ${t.text2}`}>once</span>
          </div>

          <div className={CAPTION_BLOCK}>
            <p className={`text-sm ${t.text2}`}>{priceCaption}</p>
            <p className={`text-xs mt-1 leading-relaxed ${t.text3}`}>
              Pay once and Pro is yours for good. No subscription, no renewals.
            </p>
          </div>

          <PricingFeatures t={t} items={PRO_FEATURES} />
          {/* aria-disabled rather than disabled: the button stays in the tab
              order so keyboard and screen reader users still hear that Pro is
              coming soon. With no onClick it does nothing when pressed. */}
          <button
            type="button"
            onClick={PAYMENTS_ENABLED ? () => onProSignIn(launchAvailable) : undefined}
            aria-disabled={!PAYMENTS_ENABLED}
            className={`w-full mt-auto py-4 rounded-xl font-bold transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 ${proCtaButtonClass}`}
          >
            {proCta}
          </button>
        </PricingCardShell>
      </StaggerReveal>
      <p className={`text-center text-sm mt-7 ${t.text2}`}>
        <Lock size={15} className="inline-block align-[-2px] mr-1.5 text-violet-500" />
        Neither plan links bank or QuickBooks accounts. That&apos;s by design, to keep your data private.
      </p>
      {PAYMENTS_ENABLED && (
        <p className={`text-center text-xs mt-4 ${t.text3}`}>
          Try everything on Free first. If Pro isn&apos;t for you, get a full refund within {REFUND_DAYS} days.
          <br />
          Pro is for US customers only.
        </p>
      )}
    </div>
  </section>
  );
};

const CTASection: React.FC<{ t: Tokens; onSignIn: () => void }> = ({ t, onSignIn }) => (
  <section className={`pt-14 md:pt-20 pb-10 md:pb-14 ${SECTION_PX} text-center`}>
    <ScrollReveal>
      <div className="max-w-2xl mx-auto">
        <h2 className={`text-4xl md:text-5xl font-black tracking-tight mb-4 md:mb-5 ${t.text1}`}>Ready to take control?</h2>
        <p className={`text-lg md:text-xl mb-5 md:mb-6 max-w-lg mx-auto ${t.text2}`}>
          Start tracking your finances the way they should be: intentional, private, beautiful, and yours.
        </p>
        <motion.button
          onClick={onSignIn}
          className={`px-10 md:px-12 py-4 md:py-5 rounded-xl font-bold text-lg md:text-xl transition-all ${ctaButtonClass}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.97 }}
        >
          Get Started Free →
        </motion.button>
      </div>
    </ScrollReveal>
  </section>
);
